import React from 'react';
import { BoxGeometry, ConeGeometry, CylinderGeometry, Material, MeshBasicMaterial, Quaternion, Vector3 } from 'three';

import { RenderOrder, Segment3D } from '@paragon/three-shared';
import { assertExhaustiveSwitch } from '@paragon/utilities';

const blueMaterial = new MeshBasicMaterial({ transparent: true, color: 0x0061ff, depthTest: false });
const yellowMaterial = new MeshBasicMaterial({ transparent: true, color: 0xfff600, depthTest: false });
const greenMaterial = new MeshBasicMaterial({ transparent: true, color: 0x00ff00, depthTest: false });

const purpleMaterial = new MeshBasicMaterial({ transparent: true, color: 0x993399, depthTest: false });

const cadmiumYellowMaterial = new MeshBasicMaterial({ transparent: true, color: 0xfff600, depthTest: false });

const defaultMaterial = new MeshBasicMaterial({ transparent: true, color: 0xffffff, depthTest: false });

const wineMaterial = new MeshBasicMaterial({ transparent: true, color: 0x722f37, depthTest: false });

const headGeometry = new ConeGeometry(0.5, 1, 4);
headGeometry.rotateX(Math.PI / 2);
const tailGeometry = new CylinderGeometry(1, 1, 1, 8, 1, false);
tailGeometry.rotateX(Math.PI / 2);

const boxScalePadding = 0.2;
const boxGeometry = new BoxGeometry(1, 1, 1);

export enum VectorVisualType {
  // for plating force types and link end results
  Blue,
  Yellow,
  Green,
  // for distributed load visuals
  CadmiumYellow,
  // for point load visuals
  Wine,
  // for joint results
  Purple,
  Default,
}

function getMaterialFromLoadType(vectorVisualType: VectorVisualType) {
  switch (vectorVisualType) {
    case VectorVisualType.Blue:
      return blueMaterial;
    case VectorVisualType.Yellow:
      return yellowMaterial;
    case VectorVisualType.Green:
      return greenMaterial;
    case VectorVisualType.CadmiumYellow:
      return cadmiumYellowMaterial;
    case VectorVisualType.Purple:
      return purpleMaterial;
    case VectorVisualType.Default:
      return defaultMaterial;
    case VectorVisualType.Wine:
      return wineMaterial;
    default:
      assertExhaustiveSwitch(vectorVisualType);
  }
}

export interface Props {
  readonly segment: Segment3D;
  readonly thickness: number;
  readonly vectorVisualType: VectorVisualType;
  readonly withBox?: boolean;
  readonly boxMaterial?: Material;
}

export function VectorVisual({ segment, thickness, vectorVisualType, withBox, boxMaterial }: Props) {
  const { start, end } = segment;

  const vector = new Vector3(end.x, end.y, end.z).sub(new Vector3(start.x, start.y, start.z));
  const direction = new Vector3().copy(vector).normalize();
  const center = new Vector3().copy(vector).setLength(vector.length() * 0.5);
  const quaternion = new Quaternion().setFromUnitVectors(new Vector3(0, 0, 1), direction);
  const position = new Vector3(start.x, start.y, start.z).add(center);

  const headLength = 1;
  const length = new Vector3(start.x, start.y, start.z).distanceTo(new Vector3(end.x, end.y, end.z));

  return (
    <object3D position={position.toArray()} quaternion={quaternion.toArray()}>
      <mesh
        geometry={headGeometry}
        material={getMaterialFromLoadType(vectorVisualType)}
        scale={[1, 1, headLength]}
        position={[0, 0, vector.length() / 2 - headLength / 2]}
        renderOrder={RenderOrder.VectorVisual}
      />
      <mesh
        geometry={tailGeometry}
        material={getMaterialFromLoadType(vectorVisualType)}
        scale={[thickness / 2, thickness / 2, length - headLength]}
        position={[0, 0, -headLength / 2]}
        renderOrder={RenderOrder.VectorVisual}
      />
      {withBox ? (
        <mesh
          geometry={boxGeometry}
          material={boxMaterial}
          scale={[1 + boxScalePadding, 1 + boxScalePadding, length]}
        />
      ) : null}
    </object3D>
  );
}
